import Phaser from "phaser";
import VenueDefinition from "./VenueDefinition";
import {IGameContext} from "../GameContext";

type Container = Phaser.GameObjects.Container
type Sprite = Phaser.GameObjects.Sprite
type BaseSound = Phaser.Sound.BaseSound
type Tilemap = Phaser.Tilemaps.Tilemap
type StaticTilemapLayer = Phaser.Tilemaps.StaticTilemapLayer
type Body = Phaser.Physics.Arcade.Body;

export class PlayerProfileInfo {
    constructor(
        public name:string,
        public company:string,
        public bio:string,
        public image_url:string,
        ) {
    }
}

export interface AppContext {
    startTalking(key: string): void

    showPlayerInfo(info: PlayerProfileInfo, callback?:() => void): void

    cancelTalking(): void

    currentUserId: number

    gameContext: IGameContext
}

export default class BaseWorldScene extends Phaser.Scene {
    currentX: number = -10000
    currentY: number = -10000
    sceneDone = false
    clunk: BaseSound | null = null
    map: Tilemap | null = null
    obstacles: StaticTilemapLayer | null = null
    gamePlayer: GamePlayer | null = null
    onDoneTouching: () => void = () => {
    }
    collides: WorldSceneCollide[] = []
    cursors: Cursors | null = null
    oldPosition: OldPosition | null = null

    constructor(
        public worldKey: string,
        public mapKey: string,
        public backOnBounds: boolean,
        public appContext: AppContext,
        public venue: VenueDefinition) {
        super({
            key: worldKey
        });
    }

    create() {
        this.input.keyboard.on('keydown', (event: KeyboardEvent) => {
            if (
                (event.key && event.key.length === 1) &&
                (
                    (event.key >= 'a' && event.key <= 'z') ||
                    (event.key >= 'A' && event.key <= 'Z') ||
                    event.key === ' '
                )
            ) {
                this.appContext.startTalking(event.key)
            }
        });

        this.sceneDone = false

        if (!this.clunk) {
            this.clunk = this.sound.add('clunk');
        }

        // create player animations
        this.createAnimations();

        // create map
        this.createMap();

        this.gamePlayer = this.createPlayer()

        this.cursors = {
            left: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
            right: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
            up: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
            down: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN)
        };
    }

    createMap() {
        // create the map
        this.map = this.make.tilemap({
            key: this.mapKey
        });

        // first parameter is the name of the tilemap in tiled
        let tiles = this.map.addTilesetImage('tileset-32px-extruded', 'maptiles', 32, 32);
        let ground = this.map.addTilesetImage('ground', 'ground', 32, 32);
        let structures = this.map.addTilesetImage('structures', 'structures', 32, 32);
        let woods = this.map.addTilesetImage('woods', 'woods', 32, 32);

        const allTilesets = [tiles, ground, structures, woods]

        // creating the layers
        this.map.createStaticLayer('Below Player', allTilesets, 0, 0);
        this.map.createStaticLayer('Text Layer', allTilesets, 0, 0);
        this.map.createStaticLayer('Secret Layer', allTilesets, 0, 0);
        this.obstacles = this.map.createStaticLayer('World', allTilesets, 0, 0);
        const abovePlayer = this.map.createStaticLayer('Above Player', allTilesets, 0, 0);

        abovePlayer.setDepth(10);

        // make all tiles in obstacles collidable
        this.obstacles.setCollisionByExclusion([-1]);

        // don't go out of the map
        this.physics.world.bounds.width = this.map.widthInPixels;
        this.physics.world.bounds.height = this.map.heightInPixels;

        if (this.currentX === -10000) {
            const spawnPoint = this.map.filterObjects("Objects", (obj) => {
                return obj.type === "SpawnPoint"
            })[0] as Phaser.GameObjects.Shape
            this.currentX = spawnPoint.x
            this.currentY = spawnPoint.y
        }

        this.processMapFeatures(this.map)
    }

    processMapFeatures(map: Tilemap){

        map.createFromObjects("Objects", 326, {key:""})
        const textSignBoxes = map.filterObjects("Objects", (obj) => {
            return obj.type === "TextSign"
        })

        const worldChangeBoxes = map.filterObjects("Objects", (obj) => {
            return obj.type === "WorldChange"
        })

        const floorTextBoxes = map.filterObjects("Objects", (obj) => {
            return obj.type === "FloorText"
        })

        const floorSignVenueValue = map.filterObjects("Objects", (obj) => {
            return obj.type === "FloorSignVenueValue"
        })

        textSignBoxes.forEach((obj) => this.makeTextSignTrigger(obj as Phaser.GameObjects.Shape))
        worldChangeBoxes.forEach((obj) => this.makeWorldChangeBox(obj as Phaser.GameObjects.Shape))
        floorTextBoxes.forEach((obj) => this.makeFloorSign(obj as Phaser.GameObjects.Shape))
        floorSignVenueValue.forEach((obj) => this.makeFloorSignVenueValue(obj as Phaser.GameObjects.Shape))
    }

    makeWorldChangeBox(sb: Phaser.GameObjects.Shape) {
        const sbBox = this.physics.add.staticSprite(sb.x + (sb.width / 2), sb.y + (sb.height / 2), "empty")
        sbBox.setSize(sb.width, sb.height);
        // this.physics.world.enable(sbBox);
        this.addCollide(sbBox, () => {
            this.showWorld(sb.name, true)
        })
    }

    makeFloorSign(sb: Phaser.GameObjects.Shape){
        this.add.bitmapText(sb.x, sb.y, "gamefont_small_gray", sb.name);
    }

    makeFloorSignVenueValue(sb: Phaser.GameObjects.Shape){
        let stringValue = ""
        const venueValue = this.venue.venueValue(sb.name)
        if(venueValue)
            stringValue = venueValue

        const bt = this.add.bitmapText(sb.x, sb.y, "gamefont_small_gray", stringValue.toUpperCase());
        if(bt.width > 0)
            bt.x = bt.x - (bt.width/2)
    }

    makeTextSignTrigger(signBox: Phaser.GameObjects.Shape) {

        // let playTable = this.physics.add.staticGroup()
        const x = signBox.x + (signBox.width / 2)
        const y = (signBox.y + (signBox.height / 2)) - 100

        let textObject = this.add.text(x, y, signBox.name,
            {
                padding: 6,
                fontSize: '24px',
                fontFamily: 'courier, courier new, serif'
            });

        textObject.x = textObject.x - (textObject.width / 2)
        textObject.setShadow(3, 3, 'rgba(0,0,0,0.5)', 5);
        // textObject.style.padding = 8
        // textObject.style.fontSize = '46px'

        textObject.setBackgroundColor("#aa6600")
        textObject.visible = false
        textObject.setDepth(100)

        const sbBox = this.physics.add.staticSprite(signBox.x + 16, signBox.y + 16, "empty")
        sbBox.setSize(signBox.width, signBox.height);

        this.addCollide(sbBox, () => {
            textObject.visible = true
            this.onDoneTouching = () => textObject.visible = false
        })
    }

    addCollide(sprite: Phaser.GameObjects.Sprite, callback: () => void) {
        this.collides.push(new WorldSceneCollide(sprite, callback))
    }

    createAnimations() {

        this.anims.create({
            key: 'tv_playing',
            frames: this.anims.generateFrameNumbers('tv', {
                frames: [0, 1, 2, 0, 1, 2, 0, 1, 2, 3, 4, 5, 6, 3, 4, 5, 6, 7, 8]
            }),
            frameRate: 2,
            repeat: -1
        });

        //  animation with key 'left', we don't need left and right as we will use one and flip the sprite
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', {
                frames: [1, 7, 1, 13]
            }),
            frameRate: 10,
            repeat: -1
        });

        // animation with key 'right'
        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', {
                frames: [1, 7, 1, 13]
            }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'up',
            frames: this.anims.generateFrameNumbers('player', {
                frames: [2, 8, 2, 14]
            }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'down',
            frames: this.anims.generateFrameNumbers('player', {
                frames: [0, 6, 0, 12]
            }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'talking',
            frames: this.anims.generateFrameNumbers('chatBubbles', {
                frames: [0, 1, 2]
            }),
            frameRate: 2,
            repeat: -1
        });

    }

    createPlayer(): GamePlayer {
        // our player sprite created through the physics system
        const container = this.add.container(this.currentX, this.currentY);
        container.setSize(24, 24);
        this.physics.world.enable(container);
        container.setDepth(2)

        const player = this.add.sprite(0, 0, 'player', 6);
        player.setDepth(2)
        container.add(player)


        // don't walk on trees
        this.physics.add.collider(container, this.obstacles!, () => {

        });

        // don't go out of the map
        (container.body as Body).setCollideWorldBounds(true);

        const talkRadius = this.add.sprite(0, 0, 'talkRadius')
        // this.talkRadius.setScale(.5)
        container.add(talkRadius)
        talkRadius.visible = false
        talkRadius.setDepth(2)

        // update camera
        this.updateCamera(container);

        if (this.backOnBounds) {
            (container.body as Body).onWorldBounds = true;
            this.physics.world.on('worldbounds', () => {
                this.backWorld()
            }, this);
        }


        const chatBubble = this.add.sprite(2, -40, 'chatBubbles', 0)
        container.add(chatBubble)
        chatBubble.anims.play('talking', true);
        chatBubble.visible = false
        chatBubble.setDepth(1550)

        this.addPlayerHead(container, this.appContext.currentUserId, chatBubble)

        const pp = new GamePlayer(
            container,
            player,
            talkRadius,
            chatBubble)

        this.collides.forEach((cl) => {
            const collider = this.physics.add.collider(
                container,
                cl.sprite,
                () => {

                    if (!pp.areaTouching) {
                        pp.areaTouching = true
                        cl.callback()
                    }
                }
            );
            collider.overlapOnly = true
        })

        return pp
    }

    loadPlayerInfo(playerId:number){
        fetch(`${process.env.REACT_APP_API_CALL}/users/${playerId}`)
            .then(response => response.json())
            .then((response) => this.appContext.showPlayerInfo(response))
    }

    userProfileUrl = (id:number) => {
        return `${process.env.REACT_APP_API_CALL}/image?id=${id}`
    }

    addPlayerHead(container: Phaser.GameObjects.Container, playerId: number, chatBubble: Phaser.GameObjects.Sprite) {
        const playerTextureName = `player_${playerId}`
        const addHead = () => {

            const icon = (this.add as any).rexCircleMaskImage(0, -20, playerTextureName)
            icon.setDepth(2)
            if(icon) {
                icon.width = 50
                icon.height = 50
                icon.setInteractive()
                icon.on('pointerdown', () => {
                    this.loadPlayerInfo(playerId)
                });
                container.add(icon)
                container.remove(chatBubble)
                container.add(chatBubble)
            }
        }

        if (this.textures.exists(playerTextureName)) {
            addHead()
        } else {
            let profileImage = this.userProfileUrl(playerId)
            const loader = this.load.image(playerTextureName, profileImage)
            loader.start()

            loader.on('filecomplete', function (key: string, file: any) {
                if (key === playerTextureName) {
                    addHead()
                }
            }, this);
        }
    }

    doneTouching() {
        this.gamePlayer!.areaTouching = false
        if (this.onDoneTouching) {
            this.onDoneTouching()
            this.onDoneTouching = () => {
            }
        }
    }

    update() {
        if (this.gamePlayer) {

            const containerBody = (this.gamePlayer.container.body as Body)

            // Treat 'embedded' as 'touching' also
            if (containerBody.embedded) containerBody.touching.none = false;

            const touching = !containerBody.touching.none;
            const wasTouching = !containerBody.wasTouching.none;

            if (!touching && wasTouching) this.doneTouching()

            containerBody.setVelocity(0);

            this.processMovement(containerBody);

            // emit player movement
            let x = this.gamePlayer.container.x;
            let y = this.gamePlayer.container.y;
            let flipX = this.gamePlayer.player.flipX;
            if (this.oldPosition && (x !== this.oldPosition.x || y !== this.oldPosition.y || flipX !== this.oldPosition.flipX)) {
                this.playerMovement(x, y, flipX)
            }
            // save old position data
            this.oldPosition = new OldPosition(
                this.gamePlayer.container.x,
                this.gamePlayer.container.y,
                this.gamePlayer.player.flipX)

            this.otherUpdate()
        }
    }

    otherUpdate() {

    }

    processMovement(containerBody: Phaser.Physics.Arcade.Body) {

        if(this.gamePlayer) {
            // Horizontal movement
            if (this.cursors!.left.isDown) {
                containerBody.setVelocityX(-180);
            } else if (this.cursors!.right.isDown) {
                containerBody.setVelocityX(180);
            }

            // Vertical movement
            if (this.cursors!.up.isDown) {
                containerBody.setVelocityY(-180);
            } else if (this.cursors!.down.isDown) {
                containerBody.setVelocityY(180);
            }

            // Update the animation last and give left/right animations precedence over up/down animations
            if (this.cursors!.left.isDown) {
                this.appContext.cancelTalking()
                this.gamePlayer.player.anims.play('left', true);
                this.gamePlayer.player.flipX = true;
            } else if (this.cursors!.right.isDown) {
                this.appContext.cancelTalking()
                this.gamePlayer.player.anims.play('right', true);
                this.gamePlayer.player.flipX = false;
            } else if (this.cursors!.up.isDown) {
                this.appContext.cancelTalking()
                this.gamePlayer.player.anims.play('up', true);
            } else if (this.cursors!.down.isDown) {
                this.appContext.cancelTalking()
                this.gamePlayer.player.anims.play('down', true);
            } else {
                this.gamePlayer.player.anims.stop();
            }
        }
    }

    playerMovement(x: number, y: number, flipX: boolean) {
        this.currentX = x
        this.currentY = y
    }

    showWorld(key: string, clearStack: boolean) {
        if (clearStack) {
            worldStack = []
        }
        worldStack.push({key: this.worldKey})
        this.shutDown()
        this.scene.start(key);
        this.appContext.gameContext.setCurrentRoom(key)
    }

    updateCamera(container: Container) {
        // limit camera to map
        this.cameras.main.setBounds(0, 0, this.map!.widthInPixels, this.map!.heightInPixels);
        this.cameras.main.startFollow(container);
        this.cameras.main.roundPixels = true; // avoid tile bleed
    }

    backWorld() {
        if (!this.sceneDone) {
            this.sceneDone = true
            let worldInfo = worldStack.pop()
            this.shutDown()
            if (worldInfo) {
                this.scene.start(worldInfo.key);
                this.appContext.gameContext.setCurrentRoom(worldInfo.key)
            }
        }
    }

    shutDown() {
        this.input.keyboard.removeKey(this.cursors!.left)
        this.input.keyboard.removeKey(this.cursors!.right)
        this.input.keyboard.removeKey(this.cursors!.up)
        this.input.keyboard.removeKey(this.cursors!.down)
    }
}

interface WorldKeyHolder {
    key: string
}

let worldStack: WorldKeyHolder[] = []

class WorldSceneCollide {
    constructor(public sprite: Phaser.GameObjects.Sprite, public callback: () => void) {
    }
}

export class GamePlayer {
    areaTouching = false

    constructor(
        public container: Container,
        public player: Sprite,
        public talkRadius: Sprite | null,
        public chatBubble: Sprite
    ) {
    }
}

interface Cursors {
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
}

class OldPosition {
    constructor(public x: number, public y: number, public flipX: boolean) {
    }
}
